/**
 * Batch 2 PR #5 — DSAR (Data Subject Access Request) self-service
 * portal contracts.
 *
 * Wire shapes for the publicly reachable DSAR portal that backs the
 * GDPR Art. 12–22 (and CPRA / LGPD / PDPA cousins) statutory rights:
 * access, rectification, erasure, restriction, portability,
 * objection. Every shape here is shared verbatim between
 * `routes/dsar-public.ts` + `routes/dsar-admin.ts` + the public
 * `/privacy/data-request` landing page + the admin triage UI.
 *
 * See:
 *   - migration `151_dsar_self_service_portal.sql` (DB schema)
 *   - `packages/api/src/services/dsar-service.ts` (business logic)
 *   - `packages/api/src/routes/dsar-public.ts` (anonymous form)
 *   - `packages/api/src/routes/dsar-admin.ts` (admin triage)
 *   - `docs/handbook/runbooks/dsar-portal.md` (operator runbook)
 *
 * Hard rules:
 *   - `ticketReference` is a UUID minted server-side; clients NEVER
 *     supply it. It is the only public-facing handle for a DSAR row.
 *     The row's `id` is never returned by public endpoints.
 *   - `submitterEmail` is mandatory because every status update is
 *     emailed there (Art. 12(1) "intelligible and easily accessible
 *     form"). The form deliberately does NOT require `submitterName`
 *     so we honour the data-minimisation principle (Art. 5(1)(c)).
 *   - The 30-day Art. 12(3) clock is server-controlled; clients
 *     never write `dueAt` or `extendedAt`.
 *   - Only admins / owners can patch `status`, `assignedUserId`,
 *     `responsePayload`, `internalNotes`, or trigger an extension.
 */

import { z } from 'zod';

// ============================================================================
// Enums & primitive shapes
// ============================================================================

export const DsarRequestTypeSchema = z.enum([
  /** GDPR Art. 15 / CCPA right to know. */
  'access',
  /** GDPR Art. 16. */
  'rectification',
  /** GDPR Art. 17 / CCPA right to delete. */
  'erasure',
  /** GDPR Art. 18. */
  'restriction',
  /** GDPR Art. 20. */
  'portability',
  /** GDPR Art. 21. */
  'objection',
  /** Catch-all — operator clarifies during triage. */
  'other',
]);
export type DsarRequestType = z.infer<typeof DsarRequestTypeSchema>;

export const DsarStatusSchema = z.enum([
  'received',
  'awaiting_clarification',
  'in_progress',
  'fulfilled',
  'rejected',
  'cancelled',
]);
export type DsarStatus = z.infer<typeof DsarStatusSchema>;

export const DsarSourceSchema = z.enum(['public_form', 'authenticated_user', 'admin_proxy']);
export type DsarSource = z.infer<typeof DsarSourceSchema>;

// Free-shape JSONB payload. We typecheck the keys we care about and
// let everything else through so future response types can extend
// without a contract bump.
export const DsarResponsePayloadSchema = z
  .object({
    /** For access / portability — the data_export_jobs row to grab. */
    exportJobId: z.string().uuid().optional(),
    /** For access / portability — pre-signed download URL (24h). */
    downloadUrl: z.string().url().optional(),
    /** For erasure — anonymisation certificate id. */
    deletionRef: z.string().uuid().optional(),
    /** For rectification — what changed. */
    rectification: z
      .object({
        field: z.string().min(1).max(100),
        before: z.string().max(1000),
        after: z.string().max(1000),
      })
      .optional(),
    /** Visible to the requester. Required when status='rejected'. */
    rejectionReason: z.string().min(1).max(2000).optional(),
    /** Operator-supplied notes returned to the requester verbatim. */
    notes: z.string().max(5000).optional(),
  })
  .passthrough();
export type DsarResponsePayload = z.infer<typeof DsarResponsePayloadSchema>;

// ============================================================================
// Admin-facing wire shape — full row.
// ============================================================================

/**
 * Full DSAR row as returned to admins. The public endpoints return
 * a strictly smaller subset (`DsarPublicView`).
 */
export const DsarAdminRowSchema = z.object({
  id: z.string().uuid(),
  ticketReference: z.string().uuid(),
  orgId: z.string().uuid().nullable(),
  submitterEmail: z.string().email(),
  submitterName: z.string().nullable(),
  submitterUserId: z.string().uuid().nullable(),
  requestType: DsarRequestTypeSchema,
  description: z.string(),
  status: DsarStatusSchema,
  source: DsarSourceSchema,
  submittedAt: z.string().datetime(),
  dueAt: z.string().datetime(),
  extendedAt: z.string().datetime().nullable(),
  extensionReason: z.string().nullable(),
  assignedUserId: z.string().uuid().nullable(),
  assignedUserName: z.string().nullable(),
  completedAt: z.string().datetime().nullable(),
  responsePayload: DsarResponsePayloadSchema,
  internalNotes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type DsarAdminRow = z.infer<typeof DsarAdminRowSchema>;

// ============================================================================
// Public surface — POST /api/public/dsar
// ============================================================================

export const SubmitDsarBodySchema = z.object({
  submitterEmail: z.string().email().max(320),
  submitterName: z.string().max(200).optional().nullable(),
  requestType: DsarRequestTypeSchema,
  description: z.string().min(10).max(5000),
  /**
   * Optional — if the requester knows which org the data sits in,
   * they paste the org slug. Helps triage but never required.
   * Server validates the slug exists; if not, the row lands
   * unassigned and platform_admin routes it manually.
   */
  orgSlug: z.string().min(1).max(200).optional().nullable(),
  /**
   * Honeypot field. Bots tend to fill every input; the form
   * deliberately hides this from real users. Any non-empty value
   * results in a 200 OK + DROP at the service layer (we don't
   * tip our hand by returning a 4xx).
   */
  websiteHoneypot: z.string().max(500).optional().nullable(),
});
export type SubmitDsarBody = z.infer<typeof SubmitDsarBodySchema>;

export const SubmitDsarResponseSchema = z.object({
  /** URL-safe handle the requester pastes into the status form. */
  ticketReference: z.string().uuid(),
  /**
   * Server-controlled deadline (Art. 12(3) — defaults to +30 days).
   * Returned so the public confirmation page can render
   * "you'll hear from us by …".
   */
  dueAt: z.string().datetime(),
  status: DsarStatusSchema,
  /** Lifecycle hint surfaced in the UI ("we sent you a confirmation email"). */
  acknowledgementEmailQueued: z.boolean(),
});
export type SubmitDsarResponse = z.infer<typeof SubmitDsarResponseSchema>;

/**
 * Public-status view — what GET /api/public/dsar/:ref returns.
 * Strictly less than the admin row; deliberately omits internal
 * notes, assignee identity, and the response payload's operator-
 * facing fields.
 */
export const DsarPublicViewSchema = z.object({
  ticketReference: z.string().uuid(),
  requestType: DsarRequestTypeSchema,
  status: DsarStatusSchema,
  submittedAt: z.string().datetime(),
  dueAt: z.string().datetime(),
  extendedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  /**
   * Subset of the response payload safe to expose: downloadUrl,
   * rejectionReason, notes. Operator-facing fields (exportJobId,
   * internal IDs) are stripped server-side.
   */
  publicResponse: z
    .object({
      downloadUrl: z.string().url().optional(),
      rejectionReason: z.string().optional(),
      notes: z.string().optional(),
    })
    .nullable(),
});
export type DsarPublicView = z.infer<typeof DsarPublicViewSchema>;

// ============================================================================
// Admin surface — GET / PATCH /api/orgs/:orgId/dsar
// ============================================================================

export const ListDsarQuerySchema = z.object({
  status: DsarStatusSchema.optional(),
  requestType: DsarRequestTypeSchema.optional(),
  /** Pagination cursor (opaque, base64-JSON same as audit-search). */
  cursor: z.string().nullable().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  /** When true and caller is platform_admin, include unassigned rows. */
  includeUnassigned: z.boolean().optional(),
});
export type ListDsarQuery = z.infer<typeof ListDsarQuerySchema>;

export const ListDsarResponseSchema = z.object({
  items: z.array(DsarAdminRowSchema),
  nextCursor: z.string().nullable(),
  /** Open counts surfaced in the queue heading. */
  openCount: z.number().int().min(0),
  overdueCount: z.number().int().min(0),
});
export type ListDsarResponse = z.infer<typeof ListDsarResponseSchema>;

/**
 * Admin PATCH body — every field optional; the service enforces
 * legal transitions (`fulfilled` → anything = no-op + 409).
 *
 *   - `status`: move the row through the lifecycle.
 *   - `orgId`: triage an unassigned row to my org. Only allowed if
 *     the caller can act on that org and the row is currently
 *     unassigned (one-way assignment; reassignments require
 *     platform_admin).
 *   - `assignedUserId`: claim the row for a specific operator.
 *   - `responsePayload`: merged into existing payload (shallow).
 *   - `internalNotes`: free-form, never exposed publicly.
 *   - `extendForDays`: extend the Art. 12(3) clock. Must be ≤ 60
 *     (i.e. up to +2 months on top of the original 30-day window).
 *     `extensionReason` becomes mandatory.
 *   - `extensionReason`: required when `extendForDays` is set.
 */
export const PatchDsarBodySchema = z
  .object({
    status: DsarStatusSchema.optional(),
    orgId: z.string().uuid().optional(),
    assignedUserId: z.string().uuid().nullable().optional(),
    responsePayload: DsarResponsePayloadSchema.partial().optional(),
    internalNotes: z.string().max(5000).nullable().optional(),
    extendForDays: z.number().int().min(1).max(60).optional(),
    extensionReason: z.string().min(10).max(500).optional(),
  })
  .refine(
    (b) =>
      b.status !== undefined ||
      b.orgId !== undefined ||
      b.assignedUserId !== undefined ||
      b.responsePayload !== undefined ||
      b.internalNotes !== undefined ||
      b.extendForDays !== undefined,
    { message: 'At least one field must be provided.' },
  )
  .refine((b) => (b.extendForDays === undefined) === (b.extensionReason === undefined), {
    message: 'extendForDays and extensionReason must be set together.',
  });
export type PatchDsarBody = z.infer<typeof PatchDsarBodySchema>;

export const PatchDsarResponseSchema = z.object({
  row: DsarAdminRowSchema,
});
export type PatchDsarResponse = z.infer<typeof PatchDsarResponseSchema>;

// ============================================================================
// Authenticated self-service — POST /api/me/dsar
// ============================================================================

export const SubmitDsarSelfBodySchema = z.object({
  requestType: DsarRequestTypeSchema,
  description: z.string().min(10).max(5000),
});
export type SubmitDsarSelfBody = z.infer<typeof SubmitDsarSelfBodySchema>;

export const ListDsarSelfResponseSchema = z.object({
  items: z.array(DsarPublicViewSchema),
});
export type ListDsarSelfResponse = z.infer<typeof ListDsarSelfResponseSchema>;
